import React, { useState, useEffect } from 'react';
import { 
  ChevronLeft, 
  Menu, 
  LogOut, 
  Shield 
} from 'lucide-react';
import { getSidebarNavSections } from './sidebarConfig';
import SidebarSection from './SidebarSection';
import SidebarItem from './SidebarItem';
import { API_BASE_URL } from '../../config.js';

const resolveLogoUrl = (url) => {
  if (!url || typeof url !== 'string' || url.trim() === '') return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }
  const cleanPath = url.startsWith('/') ? url : `/${url}`;
  return `${API_BASE_URL}${cleanPath}`;
};

const SchoolSidebar = ({
  activeTab,
  setActiveTab,
  academicActiveTab,
  setAcademicActiveTab,
  attendanceSubTab,
  setAttendanceSubTab,
  feesSubTab,
  setFeesSubTab,
  libraryActiveTab,
  setLibraryActiveTab,
  reportsSubTab,
  setReportsSubTab,
  studentsMenuExpanded,
  setStudentsMenuExpanded,
  academicsMenuExpanded,
  setAcademicsMenuExpanded,
  attendanceMenuExpanded,
  setAttendanceMenuExpanded,
  feesMenuExpanded,
  setFeesMenuExpanded,
  libraryMenuExpanded,
  setLibraryMenuExpanded,
  reportsMenuExpanded,
  setReportsMenuExpanded,
  showModal,
  setShowModal,
  showBulkAddModal,
  openBulkAddModal,
  openAddModal,
  orgData,
  user,
  userRole,
  permissions,
  onLogout
}) => {
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('erp_sidebar_collapsed') === 'true';
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  // Pure School ERP configuration
  const schoolName = orgData?.orgName || orgData?.name || orgData?.institutionName || 'School ERP';
  const academicYear = orgData?.academicYear || orgData?.activeAcademicYear || '';
  const initialLogo = orgData?.logo || orgData?.schoolLogo || orgData?.logoUrl || orgData?.institutionLogo || localStorage.getItem('schoolLogo') || localStorage.getItem('orgLogo') || localStorage.getItem('logoUrl') || null;

  const [logoSrc, setLogoSrc] = useState(initialLogo);
  const [logoLoadError, setLogoLoadError] = useState(false);

  useEffect(() => {
    const raw = orgData?.logo || orgData?.schoolLogo || orgData?.logoUrl || orgData?.institutionLogo || orgData?.image || localStorage.getItem('schoolLogo') || localStorage.getItem('orgLogo') || localStorage.getItem('logoUrl') || localStorage.getItem('image');
    if (raw) {
      setLogoSrc(raw);
      setLogoLoadError(false);
    } else {
      // Try fetching branding info from server
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      if (token) {
        fetch(`${API_BASE_URL}/api/admin/college/branding`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'ngrok-skip-browser-warning': 'true'
          }
        })
          .then(res => res.ok ? res.json() : null)
          .then(data => {
            if (data?.branding?.logoUrl) {
              setLogoSrc(data.branding.logoUrl);
              setLogoLoadError(false);
            }
          })
          .catch(() => {});
      }
    }
  }, [orgData]);

  const navSections = getSidebarNavSections('SCHOOL');

  // Persist collapsed state
  const toggleCollapse = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('erp_sidebar_collapsed', String(next));
      return next;
    });
  };

  // Keyboard Escape listener to close mobile menu
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Accordion state mapper
  const isSectionExpanded = (itemId) => {
    switch (itemId) {
      case 'students':
        return studentsMenuExpanded;
      case 'academics':
        return academicsMenuExpanded;
      case 'attendance':
        return attendanceMenuExpanded;
      case 'fees':
        return feesMenuExpanded;
      case 'library':
        return libraryMenuExpanded;
      case 'reports':
        return reportsMenuExpanded;
      default:
        return false;
    }
  };

  // Single-open accordion logic: Only ONE major expandable module is expanded at a time on desktop
  const collapseOtherSections = (targetId) => {
    if (targetId !== 'students' && activeTab !== 'students') setStudentsMenuExpanded(false);
    if (targetId !== 'academics' && activeTab !== 'academics') setAcademicsMenuExpanded(false);
    if (targetId !== 'attendance' && activeTab !== 'attendance') setAttendanceMenuExpanded(false);
    if (targetId !== 'fees' && activeTab !== 'fees') setFeesMenuExpanded(false);
    if (targetId !== 'library' && activeTab !== 'library') setLibraryMenuExpanded(false);
    if (targetId !== 'reports' && activeTab !== 'reports') setReportsMenuExpanded(false);
  };

  const toggleSectionExpanded = (itemId) => {
    switch (itemId) {
      case 'students':
        setStudentsMenuExpanded(!studentsMenuExpanded);
        break;
      case 'academics':
        setAcademicsMenuExpanded(!academicsMenuExpanded);
        break;
      case 'attendance':
        setAttendanceMenuExpanded(!attendanceMenuExpanded);
        break;
      case 'fees':
        setFeesMenuExpanded(!feesMenuExpanded);
        break;
      case 'library':
        setLibraryMenuExpanded(!libraryMenuExpanded);
        break;
      case 'reports':
        setReportsMenuExpanded(!reportsMenuExpanded);
        break;
      default:
        break;
    }
  };

  // Click handler for Parent Nav Items
  const handleItemClick = (item) => {
    if (collapsed && item.children && item.children.length > 0) {
      setCollapsed(false);
      localStorage.setItem('erp_sidebar_collapsed', 'false');
    }

    if (item.children && item.children.length > 0) {
      collapseOtherSections(item.id);

      if (activeTab === item.activeTab) {
        toggleSectionExpanded(item.id);
      } else {
        setActiveTab(item.activeTab);
        switch (item.id) {
          case 'students':
            setStudentsMenuExpanded(true);
            break;
          case 'academics':
            setAcademicsMenuExpanded(true);
            if (!academicActiveTab) setAcademicActiveTab('classes');
            break;
          case 'attendance':
            setAttendanceMenuExpanded(true);
            if (!attendanceSubTab) setAttendanceSubTab('student');
            break;
          case 'fees':
            setFeesMenuExpanded(true);
            if (!feesSubTab) setFeesSubTab('management');
            break;
          case 'library':
            setLibraryMenuExpanded(true);
            if (!libraryActiveTab) setLibraryActiveTab('inventory');
            break;
          case 'reports':
            setReportsMenuExpanded(true);
            if (!reportsSubTab) setReportsSubTab('analytics');
            break;
          default:
            break;
        }
      }
    } else {
      collapseOtherSections(item.id);
      setActiveTab(item.activeTab);
      if (mobileOpen) setMobileOpen(false);
    }
  };

  // Click handler for Submenu Links
  const handleSubItemClick = (sub) => {
    setActiveTab(sub.activeTab);

    if (sub.subTab) {
      switch (sub.activeTab) {
        case 'academics':
          setAcademicActiveTab(sub.subTab);
          break;
        case 'attendance':
          setAttendanceSubTab(sub.subTab);
          break;
        case 'fees':
          setFeesSubTab(sub.subTab);
          break;
        case 'library':
          setLibraryActiveTab(sub.subTab);
          break;
        case 'reports':
          setReportsSubTab(sub.subTab);
          break;
        default:
          break;
      }
    }

    if (sub.actionType) {
      if (sub.actionType === 'student_list') {
        if (setShowModal) setShowModal(false);
      } else if (sub.actionType === 'add_student') {
        if (openAddModal) openAddModal();
      } else if (sub.actionType === 'bulk_add') {
        if (openBulkAddModal) openBulkAddModal();
      }
    }

    if (mobileOpen) setMobileOpen(false);
  };

  // Fail-closed RBAC Permission Filter
  const isItemAuthorized = (item) => {
    // Items without permission requirement are always visible (e.g. Dashboard)
    if (!item.permission && item.id === 'dashboard') return true;

    const isSubUser = orgData?.userType === 'SUB_USER' || localStorage.getItem('userType') === 'SUB_USER';

    // Primary Organization Admin and Super Admin have unrestricted view
    if (!isSubUser && (userRole === 'COLLEGE_ADMIN' || userRole === 'SUPER_ADMIN')) {
      return true;
    }

    // Sub-User (Staff) strict permission check
    if (isSubUser) {
      if (!permissions || !Array.isArray(permissions) || permissions.length === 0) {
        return item.id === 'dashboard';
      }

      const permList = permissions.map(p => String(p).toLowerCase().trim());
      const itemId = (item.id || '').toLowerCase();
      const activeTab = (item.activeTab || '').toLowerCase();
      const perm = (item.permission || '').toLowerCase();
      const mod = perm ? perm.split('.')[0] : '';

      // Direct matches
      if (perm && permList.includes(perm)) return true;
      if (mod && permList.includes(mod)) return true;
      if (itemId && permList.includes(itemId)) return true;
      if (activeTab && permList.includes(activeTab)) return true;

      // Special aliases mapping backend module names to sidebar item definitions
      if (itemId === 'marks-cards' || activeTab === 'marks-cards') {
        if (permList.includes('examination') || permList.includes('examinations')) return true;
      }
      if (itemId === 'vehicle' || activeTab === 'vehicle') {
        if (permList.includes('transport') || permList.includes('vehicle')) return true;
      }
      if (itemId === 'communication' || activeTab === 'communication') {
        if (permList.includes('communication') || permList.includes('smart-alerts')) return true;
      }
      if (itemId === 'certificates' || activeTab === 'certificates') {
        if (permList.includes('certificates') || permList.includes('certificate')) return true;
      }
      if (itemId === 'id-cards' || activeTab === 'id-cards') {
        if (permList.includes('id-cards') || permList.includes('idcards')) return true;
      }

      return false;
    }

    // Default admin roles for non-subuser
    if (userRole === 'COLLEGE_ADMIN' || userRole === 'SUPER_ADMIN' || userRole === 'SCHOOL_ADMIN' || userRole === 'PRINCIPAL') {
      return true;
    }

    if (!permissions || !Array.isArray(permissions) || permissions.length === 0) return false;
    if (permissions.includes(item.permission)) return true;
    const moduleName = item.permission.split('.')[0];
    if (permissions.includes(moduleName)) return true;
    return false;
  };

  return (
    <>
      {/* Top Mobile Header Bar (< 768px) */}
      <div 
        className="mobile-header-bar"
        style={{
          display: 'none',
          height: '52px',
          background: '#ffffff',
          borderBottom: '1px solid #e2e8f0',
          padding: '0 14px',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}
      >
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0f172a', display: 'flex', alignItems: 'center' }}
          aria-label="Open navigation menu"
        >
          <Menu size={22} />
        </button>

        <div style={{ fontSize: '14.5px', fontWeight: '700', color: '#0f172a' }}>
          {schoolName}
        </div>

        <div style={{ width: '22px' }} />
      </div>

      {/* Backdrop overlay for Mobile Drawer */}
      {mobileOpen && (
        <div 
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.5)',
            backdropFilter: 'blur(2px)',
            zIndex: 9998
          }}
        />
      )}

      {/* 3-Part Semantic School Sidebar */}
      <aside
        className={`school-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}
        style={{
          width: collapsed ? '70px' : '260px',
          height: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'sticky',
          top: 0,
          background: '#ffffff',
          borderRight: '1px solid #e2e8f0',
          zIndex: 9999,
          transition: 'width 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          boxSizing: 'border-box'
        }}
      >
        {/* 1. Header (Fixed Top - Compact Clean School Branding) */}
        <header
          className="sidebar-header"
          style={{
            flexShrink: 0,
            minHeight: '64px',
            padding: collapsed ? '12px 8px' : '12px 14px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1, overflow: 'hidden' }}>
            {/* School Logo or Initials Avatar */}
            {logoSrc && !logoLoadError ? (
              <div style={{
                width: '38px',
                height: '38px',
                borderRadius: '10px',
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                padding: '2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                overflow: 'hidden'
              }}>
                <img
                  src={resolveLogoUrl(logoSrc)}
                  alt={schoolName}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  onError={() => setLogoLoadError(true)}
                />
              </div>
            ) : (
              <div style={{
                width: '38px',
                height: '38px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontWeight: '800',
                fontSize: '16px',
                boxShadow: '0 2px 4px rgba(37,99,235,0.2)'
              }}>
                {schoolName && schoolName !== 'School ERP' ? schoolName.trim().charAt(0).toUpperCase() : <Shield size={20} strokeWidth={2} />}
              </div>
            )}

            {!collapsed && (
              <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                <div
                  title={schoolName}
                  style={{
                    fontSize: '13.5px',
                    fontWeight: '800',
                    color: '#0f172a',
                    letterSpacing: '-0.2px',
                    lineHeight: '1.25',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    cursor: 'default'
                  }}
                >
                  {schoolName}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>
                    School ERP
                  </span>
                  {academicYear && (
                    <span style={{
                      display: 'inline-block',
                      fontSize: '9.5px',
                      fontWeight: '700',
                      color: '#1d4ed8',
                      background: '#eff6ff',
                      padding: '1px 5px',
                      borderRadius: '4px',
                      border: '1px solid #bfdbfe'
                    }}>
                      {academicYear}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={toggleCollapse}
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748b',
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'all 0.15s ease'
            }}
            title={collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
            aria-label="Toggle Sidebar"
          >
            <ChevronLeft
              size={15}
              style={{
                transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 200ms ease'
              }}
            />
          </button>
        </header>

        {/* 2. Navigation Area (Flex 1 - Independent Scrollable Area) */}
        <nav
          className="sidebar-navigation"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '6px 0',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {navSections.map((section) => {
            const authorizedItems = section.items.filter(isItemAuthorized);
            if (authorizedItems.length === 0) return null;

            return (
              <React.Fragment key={section.id}>
                <SidebarSection title={section.title} collapsed={collapsed} />

                {authorizedItems.map((item) => {
                  const isActive = activeTab === item.activeTab;
                  const isExpanded = isSectionExpanded(item.id);

                  return (
                    <SidebarItem
                      key={item.id}
                      item={item}
                      isActive={isActive}
                      isExpanded={isExpanded}
                      collapsed={collapsed}
                      activeSubTab={
                        item.id === 'academics' ? academicActiveTab :
                        item.id === 'attendance' ? attendanceSubTab :
                        item.id === 'fees' ? feesSubTab :
                        item.id === 'library' ? libraryActiveTab :
                        item.id === 'reports' ? reportsSubTab : null
                      }
                      currentAction={
                        showBulkAddModal ? 'bulk_add' :
                        showModal ? 'add_student' : 'student_list'
                      }
                      onItemClick={handleItemClick}
                      onSubItemClick={handleSubItemClick}
                    />
                  );
                })}
              </React.Fragment>
            );
          })}
        </nav>

        {/* 3. Footer (Fixed Bottom - Clean Account & Logout Area) */}
        <footer
          className="sidebar-footer"
          style={{
            flexShrink: 0,
            padding: collapsed ? '10px 6px' : '10px 12px',
            borderTop: '1px solid #e2e8f0',
            background: '#ffffff',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}
        >
          {/* Authenticated User Account Row (No Outer Box Border) */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: collapsed ? '4px 0' : '2px 4px'
          }}>
            <div style={{
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              background: '#e0e7ff',
              color: '#3730a3',
              fontWeight: '700',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              {(user?.name || 'A').charAt(0).toUpperCase()}
            </div>

            {!collapsed && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user?.name || 'Administrator'}
                </div>
                <div style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {(!user?.role || user.role === 'COLLEGE_ADMIN' || user.role === 'SCHOOL_ADMIN') ? 'SCHOOL_ADMIN' : user.role.replace('_', ' ')}
                </div>
              </div>
            )}
          </div>

          {/* Compact Red Logout CTA Row */}
          <button
            type="button"
            onClick={onLogout}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              width: '100%',
              height: '34px',
              padding: collapsed ? '0' : '0 8px',
              fontSize: '12.5px',
              fontWeight: '600',
              color: '#dc2626',
              background: '#fef2f2',
              border: '1px solid #fee2e2',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              gap: '8px'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#fee2e2'}
            onMouseOut={(e) => e.currentTarget.style.background = '#fef2f2'}
            title="Logout"
            aria-label="Logout"
          >
            <LogOut size={16} strokeWidth={2} />
            {!collapsed && <span>Logout</span>}
          </button>
        </footer>
      </aside>
    </>
  );
};

export default SchoolSidebar;
